import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import {
  InvoiceBillingReadiness,
  StripeAdapter,
} from '../../../../adapters/stripe.adapter';
import { ButtonComponent } from '../../../../components/button/button.component';
import { LocationInputComponent } from '../../../../components/location-input/location-input.component';
import { HereLocation } from '../../../../interfaces/location';
import { CountryService } from '../../../../services/country.service';
import {
  normalizeVatId,
  VatIdError,
  vatIdValidator,
} from '../../../../utils/vat-id.util';

const TRANSLATION_BASE =
  'company-tool.views.settings.company.payment.invoice-billing';

/** Placeholder control value when Stripe already has an address but no HERE place ID. */
const EXISTING_STRIPE_ADDRESS_ID = '__stripe_existing_address__';

@Component({
  selector: 'app-invoice-billing-modal',
  templateUrl: './invoice-billing-modal.component.html',
  styleUrls: ['./invoice-billing-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    LocationInputComponent,
    ModalComponent,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
})
export class InvoiceBillingModalComponent {
  private readonly modal = viewChild.required<ModalComponent>('modal');
  private readonly stripeAdapter = inject(StripeAdapter);
  private readonly countryService = inject(CountryService);

  readonly saved = output<void>();
  readonly dismissed = output<void>();
  readonly busy = signal(false);
  readonly error = signal('');
  readonly submitAttempt = signal(false);
  readonly addressLabel = signal('');

  private skipDismiss = false;

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    name: new FormControl('', { nonNullable: true }),
    hereId: new FormControl<string | null>(null, {
      validators: [Validators.required],
    }),
    addressLine2: new FormControl('', { nonNullable: true }),
    taxId: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, vatIdValidator()],
    }),
  });

  // Form controls are not signals, so mirror the values that error messages depend on.
  private readonly emailValue = toSignal(
    this.form.controls.email.valueChanges,
    {
      initialValue: '',
    },
  );
  private readonly hereIdValue = toSignal(
    this.form.controls.hereId.valueChanges,
    { initialValue: null },
  );
  private readonly taxIdValue = toSignal(
    this.form.controls.taxId.valueChanges,
    {
      initialValue: '',
    },
  );

  readonly emailError = computed<string>(() => {
    this.emailValue();
    if (!this.submitAttempt() || this.form.controls.email.valid) {
      return '';
    }
    return `${TRANSLATION_BASE}.errors.email`;
  });

  readonly addressError = computed<string>(() => {
    this.hereIdValue();
    if (!this.submitAttempt() || this.form.controls.hereId.valid) {
      return '';
    }
    return `${TRANSLATION_BASE}.errors.address`;
  });

  readonly taxIdError = computed<string>(() => {
    this.taxIdValue();
    if (!this.submitAttempt() || this.form.controls.taxId.valid) {
      return '';
    }
    if (this.form.controls.taxId.hasError('required')) {
      return `${TRANSLATION_BASE}.errors.tax-id-required`;
    }
    if (this.form.controls.taxId.hasError(VatIdError.FORMAT)) {
      return `${TRANSLATION_BASE}.errors.tax-id-format`;
    }
    return '';
  });

  /**
   * Opens the modal and prefills the form from the current Stripe billing state.
   * @param {InvoiceBillingReadiness} readiness Prefill data
   * @returns {void}
   */
  open(readiness: InvoiceBillingReadiness): void {
    const canReuseAddress =
      !!readiness.hereId || this.isCompleteExistingAddress(readiness);
    const addressLabel = canReuseAddress
      ? this.formatAddressLabel(readiness)
      : '';
    this.addressLabel.set(addressLabel);
    this.form.reset({
      email: readiness.email ?? '',
      name: readiness.name ?? '',
      hereId:
        readiness.hereId ?? (addressLabel ? EXISTING_STRIPE_ADDRESS_ID : null),
      addressLine2: readiness.address?.line2 ?? '',
      taxId: readiness.taxId ?? '',
    });
    this.submitAttempt.set(false);
    this.error.set('');
    this.busy.set(false);
    this.skipDismiss = false;
    this.modal().open();
  }

  /**
   * Emits dismissed when the modal is closed without a successful save.
   * @returns {void}
   */
  onModalClosed(): void {
    if (this.skipDismiss) {
      this.skipDismiss = false;
      return;
    }
    this.dismissed.emit();
  }

  /**
   * Closes the modal.
   * @returns {void}
   */
  close(): void {
    this.modal().close();
  }

  /**
   * Stores the selected address label for the location input.
   * @param {HereLocation | null} location Selected location
   * @returns {void}
   */
  onLocationChange(location: HereLocation | null): void {
    if (location?.id === EXISTING_STRIPE_ADDRESS_ID) {
      return;
    }
    if (location && location.valid === false) {
      this.form.controls.hereId.setValue(null);
      this.addressLabel.set('');
      return;
    }
    this.addressLabel.set(location?.label ?? '');
  }

  /**
   * Saves the billing details to Stripe.
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    this.submitAttempt.set(true);
    this.form.markAllAsTouched();
    this.error.set('');
    if (this.form.invalid) {
      return;
    }

    this.busy.set(true);
    const { email, name, hereId, addressLine2, taxId } =
      this.form.getRawValue();
    try {
      await firstValueFrom(
        this.stripeAdapter.updateInvoiceBillingDetails({
          email: email.trim(),
          name: name.trim() || undefined,
          hereId:
            hereId && hereId !== EXISTING_STRIPE_ADDRESS_ID
              ? hereId
              : undefined,
          addressLine2: addressLine2.trim() || undefined,
          taxId: normalizeVatId(taxId),
        }),
      );
      this.skipDismiss = true;
      this.saved.emit();
      this.close();
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse ? error.error?.message : '';
      if (message === 'Company/InvalidAddress') {
        this.form.controls.hereId.setValue(null);
        this.addressLabel.set('');
        return;
      }
      this.error.set(`${TRANSLATION_BASE}.errors.save`);
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Whether the stored Stripe address has all fields required for invoice billing.
   * @param {InvoiceBillingReadiness} readiness Prefill data
   * @returns {boolean} True when line1, city, postal code and country are present
   */
  private isCompleteExistingAddress(
    readiness: InvoiceBillingReadiness,
  ): boolean {
    const address = readiness.address;
    return !!(
      address?.line1 &&
      address?.city &&
      address?.postal_code &&
      address?.country
    );
  }

  /**
   * Builds a readable billing address label from Stripe metadata or address fields.
   * @param {InvoiceBillingReadiness} readiness Prefill data
   * @returns {string} Address label, or an empty string when no address exists
   */
  private formatAddressLabel(readiness: InvoiceBillingReadiness): string {
    if (readiness.addressLabel?.trim()) {
      return readiness.addressLabel.trim();
    }
    const address = readiness.address;
    if (!address?.line1) {
      return '';
    }
    const cityLine = [address.postal_code, address.city]
      .filter(Boolean)
      .join(' ');
    const country = address.country
      ? this.countryService.getCountryName(address.country)
      : '';
    return [address.line1, cityLine, country].filter(Boolean).join(', ');
  }
}
